import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { forexApi } from "@/lib/forex-api";

interface ModelRecord {
  id: string;
  provider: string;
  modelName: string;
  boundAgents: number;
  calls30d: number | null;
  successRate: number | null;
  avgLatencyMs: number | null;
  costEstimate: number | null;
  status: "enabled" | "disabled" | string;
}

function asNumber(value: unknown, fallback = 0) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function observedNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asString(value: unknown, fallback = "--") {
  return typeof value === "string" && value ? value : fallback;
}

export default function ModelDashboardPage() {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState("calls30d");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [records, setRecords] = useState<ModelRecord[]>([]);
  const [summary, setSummary] = useState<Record<string, number | string>>({});
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  const showToast = (type: "success" | "error", msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setLoadFailed(false);
      try {
        const dashboard = await forexApi.getModelDashboard();
        if (!active) return;
        setSummary(dashboard.summary);
        setRecords(dashboard.items.map((item) => {
          const status = asString(item.status, "disabled");
          return {
            id: asString(item.model_id),
            provider: asString(item.provider),
            modelName: asString(item.display_name),
            boundAgents: asNumber(item.bound_agent_count),
            calls30d: observedNumber(item.monthly_calls),
            successRate: observedNumber(item.success_rate),
            avgLatencyMs: observedNumber(item.avg_latency_ms),
            costEstimate: observedNumber(item.cost_estimate),
            status,
          };
        }));
      } catch (error) {
        if (active) {
          setLoadFailed(true);
          setSummary({});
          setRecords([]);
          showToast("error", "模型调用大盘加载失败，请检查后台服务");
        }
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, []);

  const sorted = useMemo(() => {
    return [...records].sort((a, b) => {
      const va = a[sortBy as keyof ModelRecord];
      const vb = b[sortBy as keyof ModelRecord];
      if (typeof va !== "number" || typeof vb !== "number") return 0;
      return sortDir === "desc" ? vb - va : va - vb;
    });
  }, [records, sortBy, sortDir]);

  const handleSort = (col: string) => {
    if (sortBy === col) setSortDir((dir) => (dir === "desc" ? "asc" : "desc"));
    else {
      setSortBy(col);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ col }: { col: string }) => (
    <i className={`text-[10px] ml-1 ${sortBy === col ? (sortDir === "desc" ? "ri-sort-desc" : "ri-sort-asc") : "ri-expand-up-down-line text-text-muted"}`} />
  );

  const totals = {
    models: asNumber(summary.model_total, sorted.length),
    enabled: asNumber(summary.enabled_total, sorted.filter((record) => record.status === "enabled").length),
    calls30d: observedNumber(summary.monthly_calls),
    avgLatency: observedNumber(summary.avg_latency_ms),
    avgSuccess: observedNumber(summary.success_rate),
    cost: observedNumber(summary.cost_estimate),
  };
  const metric = (value: number | null, format: (value: number) => string) =>
    loading ? "加载中" : loadFailed ? "加载失败" : value === null ? "未提供" : format(value);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">模型调用大盘</h2>
          <p className="text-xs text-text-muted mt-0.5">模型资源与调用观测；未提供的统计不代表零调用或健康</p>
        </div>
        <button onClick={() => navigate("/forex/model-config")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-settings-3-line text-sm" />模型配置
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">模型总数</p><p className="text-sm font-semibold font-mono mt-0.5 text-text-primary">{loadFailed ? "加载失败" : loading ? "加载中" : totals.models}</p></div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">已启用</p><p className="text-sm font-semibold font-mono mt-0.5 text-emerald-500">{loadFailed ? "加载失败" : loading ? "加载中" : totals.enabled}</p></div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">30d 调用量</p><p className="text-sm font-semibold font-mono mt-0.5 text-text-primary">{metric(totals.calls30d, (v) => v.toLocaleString())}</p></div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">平均成功率</p><p className="text-sm font-semibold font-mono mt-0.5 text-text-primary">{metric(totals.avgSuccess, (v) => `${v.toFixed(1)}%`)}</p></div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">平均延迟</p><p className="text-sm font-semibold font-mono mt-0.5 text-text-primary">{metric(totals.avgLatency, (v) => `${v.toFixed(0)}ms`)}</p></div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">估算成本</p><p className="text-sm font-semibold font-mono mt-0.5 text-text-primary">{metric(totals.cost, (v) => `$${v.toFixed(2)}`)}</p></div>
      </div>

      <div className="bg-white rounded-lg border border-surface-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[1200px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">Provider / Model</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px] cursor-pointer" onClick={() => handleSort("boundAgents")}>绑定<SortIcon col="boundAgents" /></th>
                <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px] cursor-pointer" onClick={() => handleSort("calls30d")}>30d 调用<SortIcon col="calls30d" /></th>
                <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px] cursor-pointer" onClick={() => handleSort("successRate")}>成功率<SortIcon col="successRate" /></th>
                <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px] cursor-pointer" onClick={() => handleSort("avgLatencyMs")}>延迟<SortIcon col="avgLatencyMs" /></th>
                <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px] cursor-pointer" onClick={() => handleSort("costEstimate")}>成本<SortIcon col="costEstimate" /></th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">健康</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">状态</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-text-muted">加载模型调用大盘...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-text-muted">{loadFailed ? "加载失败，暂无法判断模型与调用状态" : "暂无模型；调用统计未提供"}</td></tr>
              ) : sorted.map((record) => {
                return (
                  <tr key={record.id} className="border-b border-surface-border hover:bg-surface/30 transition-colors">
                    <td className="px-4 py-2.5"><div className="flex items-center gap-2"><span className="text-[10px] font-medium text-text-muted bg-surface px-1.5 py-0.5 rounded">{record.provider}</span><span className="text-text-primary text-xs font-mono whitespace-nowrap">{record.modelName}</span></div></td>
                    <td className="px-4 py-2.5 text-center text-text-primary font-mono text-xs">{record.boundAgents}</td>
                    <td className="px-4 py-2.5 text-right text-text-primary font-mono text-xs">{record.calls30d === null ? "未提供" : record.calls30d.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right text-text-primary font-mono text-xs">{record.successRate === null ? "未提供" : `${record.successRate}%`}</td>
                    <td className="px-4 py-2.5 text-right text-text-primary font-mono text-xs">{record.avgLatencyMs === null ? "未提供" : `${record.avgLatencyMs}ms`}</td>
                    <td className="px-4 py-2.5 text-right text-text-primary font-mono text-xs">{record.costEstimate === null ? "未提供" : `$${record.costEstimate.toFixed(2)}`}</td>
                    <td className="px-4 py-2.5 text-center text-text-muted">未提供</td>
                    <td className="px-4 py-2.5 text-center"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${record.status === "enabled" ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500"}`}>{record.status === "enabled" ? "已启用" : "已禁用"}</span></td>
                    <td className="px-4 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Link to={`/forex/agents/model-config/detail?id=${record.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand transition-colors cursor-pointer whitespace-nowrap"><i className="ri-eye-line text-[11px]" />详情</Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${toast.type === "success" ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}><i className={`text-base ${toast.type === "success" ? "ri-checkbox-circle-fill" : "ri-close-circle-fill"}`} />{toast.msg}</div>
        </div>
      )}
    </div>
  );
}
